import { Alert, AlertType } from 'components/Alert/Alert'
import { Box, Button, colors, ColorVariant, Flex } from 'styling/GlobalStyles'
import { BigText, MediumText, NormalText } from 'styling/TypographyV2'
import { DoubleBox } from './Components/DoubleBox'
import { useQuery } from '@tanstack/react-query'
import { getProcurementPurchaseOrders } from 'api'
import { useState } from 'react'
import {
  ProcurementPurchaseOrderResponse,
  ProcurementPurchaseOrderItemResponse,
  ProcurementPurchaseOrderStatus,
} from 'types/procurement'
import { JustSpinner } from 'components/Loading/Loading'
import { EmptyList } from 'components/Table/EmptyList'
import { Card, CardBody, StyckyFooter } from 'components/Card/Card'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'
import { Align, Table } from 'components/Table/Table'
import { ReceiveItemModal } from './Components/ReceiveItemModal'
import { toCsv } from 'utils/helpers'

export const Receiving = () => {
  const [showReceiveItemModal, setShowReceiveItemModal] =
    useState<ProcurementPurchaseOrderItemResponse | null>(null)
  const { data: orders, isLoading } = useQuery({
    queryKey: ['procurement-purchase-orders-receiving'],
    queryFn: () =>
      getProcurementPurchaseOrders({
        page: 1,
        limit: 100,
        statuses: toCsv([
          ProcurementPurchaseOrderStatus.PartiallyReceived,
          ProcurementPurchaseOrderStatus.EmailSentToSupplier,
          ProcurementPurchaseOrderStatus.SendingEmailToSupplier,
        ]),
      }),
  })
  const [activeRequest, setActiveRequest] = useState<ProcurementPurchaseOrderResponse | null>(null)
  return (
    <>
      <Alert
        title='SKUs Only'
        description='Only resolved catalog SKUs appear in receiving. Free-text items must be resolved before PO generation.'
        type={AlertType.SUCCESS}
        colorVariant={ColorVariant.PRIMARY}
        dismissKey='receiving-alert'
      />
      <DoubleBox>
        <Box
          style={{
            overflow: 'auto',
          }}
        >
          <NormalText>Purchase Request in Sourcing</NormalText>
          <Flex column mt='16' gap='0.5rem'>
            {isLoading ? (
              <Flex center justcenter height='400px'>
                <JustSpinner />
              </Flex>
            ) : orders?.data.length > 0 ? (
              orders?.data.map((item) => (
                <PurchaseRequestCard
                  key={item.id}
                  order={item}
                  selected={activeRequest?.id === item.id}
                  onClick={() => setActiveRequest(item)}
                />
              ))
            ) : (
              <Flex center justcenter height='100%'>
                <EmptyList
                  title='No Purchase Requests found'
                  description='No Purchase Requests found'
                />
              </Flex>
            )}
          </Flex>
        </Box>
        <Card
          style={{
            overflow: 'auto',
          }}
        >
          <CardBody>
            <NormalText>Supplier Offers</NormalText>
            <Flex column mt='16' gap='0.5rem'>
              {activeRequest ? (
                <ReceivedItemDetails
                  order={activeRequest}
                  setShowReceiveItemModal={setShowReceiveItemModal}
                />
              ) : (
                <Flex center justcenter height='100%'>
                  <EmptyList
                    title='No Purchase Order selected'
                    description='No Purchase Order selected'
                  />
                </Flex>
              )}
            </Flex>
          </CardBody>
        </Card>
      </DoubleBox>
      {showReceiveItemModal && (
        <ReceiveItemModal
          onClose={() => setShowReceiveItemModal(null)}
          item={showReceiveItemModal}
          order={activeRequest}
          setActiveOrder={setActiveRequest}
        />
      )}
    </>
  )
}

const PurchaseRequestCard = ({
  order,
  selected,
  onClick,
}: {
  order: ProcurementPurchaseOrderResponse
  selected: boolean
  onClick: () => void
}) => {
  const receivedQuantity = order.items.reduce((acc, item) => acc + (item.receivedQuantity || 0), 0)
  const totalQuantity = order.items.reduce(
    (acc, item) => acc + (item.procurementPurchaseRequestItem.quantity || 0),
    0,
  )
  const percentage = Math.round((receivedQuantity / totalQuantity) * 100)
  return (
    <Card
      padding='0.875rem'
      onClick={onClick}
      fullBorder={selected ? colors.orange : 'transparent'}
    >
      <CardBody>
        <Flex between>
          <Flex column mb='8'>
            <NormalText colorVariant={ColorVariant.PRIMARY} mb='0.5rem'>
              {'#PR-' + order.id}
            </NormalText>
            <BigText bold>{order.procurementPurchaseRequest.title}</BigText>
          </Flex>
        </Flex>
        <Flex between center mt='6' mb='4'>
          <NormalText colorVariant={ColorVariant.SECONDARY}>Progress</NormalText>
          <NormalText colorVariant={ColorVariant.SECONDARY}>{Math.round(percentage)}%</NormalText>
        </Flex>
        <ProgressBar percentage={percentage} color={colors.orange} />
      </CardBody>
    </Card>
  )
}

const ReceivedItemDetails = ({
  order,
  setShowReceiveItemModal,
}: {
  order: ProcurementPurchaseOrderResponse
  setShowReceiveItemModal: (item: ProcurementPurchaseOrderItemResponse) => void
}) => {
  return (
    <Table
      options={[
        {
          header: 'SKU',
          cell: (item) => (
            <NormalText colorVariant={ColorVariant.PRIMARY}>
              {item.procurementPurchaseRequestItem.inventoryItem?.sku}
            </NormalText>
          ),
        },
        {
          header: 'Item',
          cell: (item) => (
            <NormalText>{item.procurementPurchaseRequestItem.inventoryItem?.name}</NormalText>
          ),
        },
        {
          header: 'Ordered',
          cell: (item) => (
            <NormalText colorVariant={ColorVariant.SECONDARY}>
              {item.procurementPurchaseRequestItem.quantity}
            </NormalText>
          ),
        },
        {
          header: 'Received',
          cell: (item) => (
            <NormalText colorVariant={ColorVariant.SECONDARY}>{item.receivedQuantity}</NormalText>
          ),
        },
        {
          header: 'Remaining',
          cell: (item) => (
            <NormalText colorVariant={ColorVariant.PRIMARY}>
              {item.procurementPurchaseRequestItem.quantity - item.receivedQuantity}
            </NormalText>
          ),
        },
        {
          align: Align.RIGHT,
          header: 'Receive Now',
          cell: (item) => (
            <Button width='fit-content' orange onClick={() => setShowReceiveItemModal(item)}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10 4.16602V15.8327'
                  stroke='#1E1E1E'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M4.16699 10H15.8337'
                  stroke='#1E1E1E'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </Button>
          ),
        },
      ]}
      items={order.items}
    />
  )
}
